/**
 * Providers globais de contexto para a aplicação.
 *
 * Inclui:
 * - React Query (TanStack)
 * - NextThemes (dark/light)
 * - NextAuth (sessão)
 * - Sidebar/Layout
 * - Cursor customizado
 * - Toasts globais
 * - Header customizado
 *
 * @param children Elementos filhos a serem renderizados dentro dos providers
 * @returns JSX.Element
 */

"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  useState,
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { usePathname } from "next/navigation";
import { Toaster } from "react-hot-toast";
import { ThemeProvider } from "next-themes";
import {
  SidebarProvider,
  SidebarLayout,
} from "visuais";
import React from "react";
import { SessionProvider } from "next-auth/react";

// Contexto para header customizado
interface HeaderContextType {
  customHeaderComponent: ReactNode | null;
  setCustomHeaderComponent: (component: ReactNode | null) => void;
}

const HeaderContext = createContext<HeaderContextType | null>(null);

export function useHeaderContext() {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error(
      "useHeaderContext deve ser usado dentro de um HeaderProvider"
    );
  }
  return context;
}

// Hook personalizado para facilitar o uso do header customizado
export function useCustomHeader() {
  const { setCustomHeaderComponent } = useHeaderContext();

  const setHeader = useCallback(
    (component: ReactNode | null) => {
      setCustomHeaderComponent(component);
    },
    [setCustomHeaderComponent]
  );

  // Limpa o header customizado quando o componente é desmontado
  useEffect(() => {
    return () => {
      setCustomHeaderComponent(null);
    };
  }, [setCustomHeaderComponent]);

  return { setHeader };
}

// Contexto para título da página
interface PageTitleContextType {
  pageTitle: string;
  setPageTitle: (title: string) => void;
}

const PageTitleContext = createContext<PageTitleContextType | null>(null);

export function usePageTitleContext() {
  const context = useContext(PageTitleContext);
  if (!context) {
    throw new Error(
      "usePageTitleContext deve ser usado dentro de um PageTitleProvider"
    );
  }
  return context;
}

// Hook personalizado para facilitar o uso do título da página
export function usePageTitle() {
  const { setPageTitle } = usePageTitleContext();

  const suffix = process.env.NEXT_PUBLIC_ENV === "test" ? " - DEV" : "";

  const setTitle = useCallback(
    (title: string) => setPageTitle(`${title} - RodoAPP${suffix}`),
    [setPageTitle, suffix]
  );

  // Limpa o título quando o componente é desmontado
  useEffect(() => {
    return () => {
      setPageTitle(`RodoAPP${suffix}`);
    };
  }, [setPageTitle, suffix]);

  return { setTitle };
}

function HeaderProvider({ children }: { children: ReactNode }) {
  const [customHeaderComponent, setCustomHeaderComponent] =
    useState<ReactNode | null>(null);

  return (
    <HeaderContext.Provider
      value={{ customHeaderComponent, setCustomHeaderComponent }}
    >
      {children}
    </HeaderContext.Provider>
  );
}

function PageTitleProvider({ children }: { children: ReactNode }) {
  const suffix = process.env.NEXT_PUBLIC_ENV === "test" ? " - DEV" : "";
  const [pageTitle, setPageTitle] = useState<string>(`RodoAPP${suffix}`);

  // Atualiza o título do documento quando pageTitle mudar
  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  return (
    <PageTitleContext.Provider value={{ pageTitle, setPageTitle }}>
      {children}
    </PageTitleContext.Provider>
  );
}

interface ProvidersProps {
  children: React.ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const [queryClient] = useState(() => new QueryClient());
  const pathname = usePathname();

  return (
    <SessionProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider
          attribute="class"
          defaultTheme="system"
          enableSystem
          disableTransitionOnChange
        >
          <HeaderProvider>
            <PageTitleProvider>
              <SidebarProvider defaultOpen={false}>
                <Toaster
                  position="bottom-right"
                  reverseOrder={true}
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: "var(--bg-primary)",
                      color: "var(--foreground)",
                      border: "1px solid var(--border)",
                      borderRadius: "var(--radius)",
                      boxShadow:
                        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                    },
                    success: {
                      style: {
                        background: "var(--bg-success)",
                        color: "var(--foreground)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                      },
                    },
                    error: {
                      style: {
                        background: "var(--bg-error)",
                        color: "var(--foreground)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                      },
                    },
                    loading: {
                      style: {
                        background: "var(--bg-primary)",
                        color: "var(--foreground)",
                        border: "1px solid var(--border)",
                        borderRadius: "var(--radius)",
                      },
                    },
                  }}
                />
                {pathname === "/login" ? (
                  children
                ) : (
                  <SidebarLayout>{children}</SidebarLayout>
                )}
              </SidebarProvider>
            </PageTitleProvider>
          </HeaderProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SessionProvider>
  );
}
